import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, TrendingUp, Calendar, Mic, BarChart3, Users, Share2, Copy, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { isDemoMode } from "@/components/DemoBanner";

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="brutal-card bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-8 h-8 rounded-lg border-[2.5px] border-[color:var(--ink)] bg-[color:var(--gold)] flex items-center justify-center">
          <Icon size={14} />
        </span>
        <span className="display text-xs uppercase tracking-[0.2em] text-[color:var(--ink-soft)] font-bold">{label}</span>
      </div>
      <div className="display text-3xl font-extrabold">{value}</div>
      {hint && <div className="text-xs text-[color:var(--ink-soft)] mt-1">{hint}</div>}
    </div>
  );
}

function ClientCard({ profile, stats }) {
  return (
    <div className="brutal-card bg-white p-5" data-testid={`client-card-${profile.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-14 h-14 rounded-2xl border-[3px] border-[color:var(--ink)] flex items-center justify-center text-3xl"
          style={{ background: profile.color }}
        >
          {profile.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <div className="display text-base sm:text-xl font-extrabold leading-tight">{profile.name}</div>
          <div className="text-xs text-[color:var(--ink-soft)]">
            {stats?.last_active
              ? `Last active ${new Date(stats.last_active).toLocaleDateString()}`
              : "No activity yet"}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <StatCard icon={BarChart3} label="Taps (7d)" value={stats?.total_taps ?? 0} />
        <StatCard icon={Calendar} label="Days active" value={stats?.days_active ?? 0} hint="last 7 days" />
        <StatCard icon={Mic} label="Vocab tiles" value={stats?.total_tiles ?? 0} />
      </div>
      {stats?.top_tiles?.length > 0 && (
        <div className="mb-3">
          <div className="display text-xs uppercase tracking-[0.2em] text-[color:var(--ink-soft)] font-bold mb-2">Top words this week</div>
          <div className="flex flex-wrap gap-2">
            {stats.top_tiles.slice(0, 6).map((t) => (
              <span
                key={t.label}
                className="px-2 py-1 rounded-full border-[2.5px] border-[color:var(--ink)] bg-[color:var(--paper-muted)] text-sm font-semibold flex items-center gap-1"
              >
                <span aria-hidden>{t.emoji}</span>
                {t.label}
                <span className="text-xs text-[color:var(--ink-soft)]">×{t.tap_count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <Link to={`/board/${profile.id}`} className="pill-btn primary flex-1 justify-center" data-testid={`client-board-${profile.id}`}>
          Open board
        </Link>
        <Link to={`/settings/${profile.id}`} className="pill-btn" data-testid={`client-settings-${profile.id}`}>
          Settings
        </Link>
      </div>
    </div>
  );
}

function BatchShare({ profiles }) {
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [copiedAll, setCopiedAll] = useState(false);

  const toggle = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAll = () => setSelected(new Set(profiles.map((p) => p.id)));
  const clearAll = () => setSelected(new Set());

  const submit = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setError("");
    setResults([]);
    try {
      const { data } = await api.post("/therapist/batch-share", {
        profile_ids: Array.from(selected),
      });
      setResults(data.results || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const urlFor = (token) => `${window.location.origin}/import/${token}`;

  const copyAll = () => {
    const text = results
      .filter((r) => r.ok)
      .map((r) => `${r.name}: ${urlFor(r.token)}`)
      .join("\n");
    if (!text) return;
    try {
      navigator.clipboard.writeText(text);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 1500);
    } catch {
      window.prompt("Copy these share links:", text);
    }
  };

  if (!profiles?.length) return null;

  return (
    <section className="brutal-card bg-white p-5 mb-5" data-testid="batch-share-panel">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-xl border-[3px] border-[color:var(--ink)] bg-[#5AA9E6] text-white flex items-center justify-center">
          <Share2 size={16} />
        </span>
        <div>
          <h2 className="display text-xl font-bold leading-tight">Batch share boards</h2>
          <p className="text-xs text-[color:var(--ink-soft)]">
            Mint share-import links for many clients at once. Re-using a profile that already has a link returns its existing token.
          </p>
        </div>
      </div>

      <div className="brutal-card bg-[color:var(--paper-muted)] p-3 mb-3">
        <div className="flex items-center gap-2 mb-2 text-xs">
          <button type="button" onClick={selectAll} className="pill-btn" data-testid="batch-share-select-all">Select all</button>
          <button type="button" onClick={clearAll} className="pill-btn" data-testid="batch-share-clear">Clear</button>
          <span className="text-[color:var(--ink-soft)] ml-auto">
            {selected.size} of {profiles.length} selected
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-48 overflow-auto">
          {profiles.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-white rounded">
              <input
                type="checkbox"
                checked={selected.has(p.id)}
                onChange={() => toggle(p.id)}
                className="w-4 h-4 accent-[color:var(--coral)]"
                data-testid={`batch-share-pick-${p.id}`}
              />
              <span aria-hidden>{p.avatar}</span>
              <span className="truncate">{p.name}</span>
            </label>
          ))}
        </div>
      </div>

      <button
        type="button"
        disabled={busy || selected.size === 0}
        onClick={submit}
        className="pill-btn primary"
        data-testid="batch-share-submit"
      >
        <Share2 size={14} /> {busy ? "Generating…" : `Generate ${selected.size || ""} share links`}
      </button>

      {error && (
        <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-2 mt-3 text-sm" data-testid="batch-share-error">{error}</div>
      )}

      {results.length > 0 && (
        <div className="mt-4" data-testid="batch-share-results">
          <div className="flex items-center justify-between mb-2">
            <span className="display font-bold text-sm">Share links</span>
            <button type="button" onClick={copyAll} className="pill-btn" data-testid="batch-share-copy-all">
              {copiedAll ? <Check size={13} /> : <Copy size={13} />} {copiedAll ? "Copied!" : "Copy all"}
            </button>
          </div>
          <ul className="space-y-1">
            {results.map((r, i) => (
              <li key={r.profile_id || `r-${i}`} className="text-sm flex items-center gap-2">
                {r.ok ? (
                  <>
                    <span aria-hidden>📎</span>
                    <span className="font-bold truncate">{r.name}</span>
                    <code className="font-mono text-xs flex-1 truncate text-[color:var(--ink-soft)]">{urlFor(r.token)}</code>
                  </>
                ) : (
                  <>
                    <span aria-hidden>⚠️</span>
                    <span className="text-[color:var(--coral-deep)]">{r.profile_id} — {r.error}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}


export default function TherapistDashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [statsMap, setStatsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profiles");
        setProfiles(data);
        const entries = await Promise.all(
          data.map(async (p) => {
            try {
              const r = await api.get(`/profiles/${p.id}/stats?since=week`);
              return [p.id, r.data];
            } catch (err) {
              console.warn(`TherapistDashboard: stats fetch failed for ${p.id}`, err?.response?.status || err?.message);
              return [p.id, null];
            }
          }),
        );
        setStatsMap(Object.fromEntries(entries));
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
      setLoading(false);
    })();
  }, []);

  const switchToCaregiver = async () => {
    try {
      await api.put("/auth/role", { role: "caregiver" });
      await refresh();
      nav("/profiles");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Aggregate totals
  const totalClients = profiles.length;
  const totalTaps = Object.values(statsMap).reduce((s, x) => s + (x?.total_taps || 0), 0);
  const activeThisWeek = Object.values(statsMap).filter((x) => (x?.days_active || 0) > 0).length;

  return (
    <div className="min-h-screen px-4 sm:px-8 py-8 dotted-bg">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <Link to="/profiles" className="pill-btn" data-testid="therapist-back">
            <ArrowLeft size={16} />
          </Link>
          <div>
            <div className="display text-xs uppercase tracking-[0.25em] text-[color:var(--coral-deep)] font-bold flex items-center gap-2">
              <Users size={14} /> Therapist mode
            </div>
            <h1 className="display text-3xl sm:text-4xl font-extrabold" data-testid="therapist-title">
              Your clients
            </h1>
          </div>
          <div className="flex-1" />
          <button type="button" onClick={switchToCaregiver} className="pill-btn" data-testid="therapist-switch-back">
            Switch to caregiver mode
          </button>
        </div>

        <div className="brutal-card bg-[color:var(--gold)] p-3 mb-3 flex items-center gap-3 text-sm" data-testid="therapist-launch-banner">
          <span className="text-2xl">🎉</span>
          <div>
            <strong>Early access</strong> — full therapist features unlocked.
          </div>
        </div>
        {isDemoMode() && (
          <div className="brutal-card bg-[color:var(--coral)]/10 border-[3px] border-[color:var(--coral)] p-3 mb-5 text-sm" data-testid="therapist-demo-notice">
            <strong>Early access.</strong> Connecting a real clinic, school, or
            licensed clinician is a planned production feature — clients shown
            here are preview profiles only. Please don't enter real client PHI.
          </div>
        )}

        {error && (
          <div className="brutal-card bg-white p-3 mb-4 text-[color:var(--coral-deep)]">{error}</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard icon={Users}      label="Clients"        value={totalClients} />
          <StatCard icon={TrendingUp} label="Taps this week" value={totalTaps} hint="across all clients" />
          <StatCard icon={Calendar}   label="Active clients" value={activeThisWeek} hint="this week" />
        </div>

        {/* TherapistInvites panel removed: it sold the superseded paid
            professional tier, referral discounts, and purchased pass quotas —
            all struck by CANON block 2 + R4-C/R4-G (professionals never pay;
            no referral discounts; distribution is founder-controlled). The
            blueprint's professional flow is caregiver-initiated invites. */}

        <BatchShare profiles={profiles} />

        {loading ? (
          <div className="display text-2xl text-[color:var(--ink-soft)]">Loading clients…</div>
        ) : profiles.length === 0 ? (
          <div className="brutal-card bg-white p-6 text-center">
            <div className="text-4xl mb-2" aria-hidden>🌟</div>
            <div className="display text-xl font-bold mb-1">No clients yet</div>
            <p className="text-[color:var(--ink-soft)] mb-3">Create a board for each client and you'll see their usage roll up here.</p>
            <Link to="/profiles" className="pill-btn primary" data-testid="therapist-add-client">Add your first client</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {profiles.map((p) => (
              <ClientCard key={p.id} profile={p} stats={statsMap[p.id]} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
